// App.jsx

import React, { useState, useEffect, useContext, useRef } from 'react';

import PersonForm from './components/PersonForm';
import PersonTable from './components/PersonTable';
import PaymentTable from './components/PaymentTable';
import FirmManager from './components/FirmManager'; // Importiramo FirmManager

import { AuthContext } from './config/AuthProvider';
import Login from './authPage/Login';

import { db } from './config/firebase';
import { collection, getDocs, getDoc, query, where, addDoc, deleteDoc, doc, setDoc, updateDoc } from 'firebase/firestore';

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import Modal from 'react-modal'; // Importiramo Modal iz react-modal
import { addLegacyFinancialAliases, getFinancialMigrationPatch, toNumber } from './utils/obracunFields';
import { normalizeWeekAndPlatform, validateEarningRecord } from './utils/earningsValidation';
import { DEFAULT_OBRACUN_RATES } from './utils/obracunRatesDefaults';
import { EARNINGS_IMPORT_CONFIG } from './utils/excelImportConfig';
import { buildUserPeriodDataMap } from './utils/userPeriodDataUtils';
import { getPersonEarningsForMonth, sumEarningsByWeek } from './utils/obracunPersonContext';
import {
  computeAutoAdditionsFromHours,
  computeFeeTotals,
  computeMonthlyHourSummary,
  computeTotal2,
} from './utils/obracunCalculator';
import { getMonthlyFinancial, getMonthlyScalar } from './utils/userPeriodDataUtils';

import './App.css'; // Importamo CSS

function App() {
  // Postojeća stanja
  const [people, setPeople] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [userPeriodData, setUserPeriodData] = useState({});
  const [uploadedWeeks, setUploadedWeeks] = useState([]); // Stanje za praćenje učitanih tjedana

  // Novo stanje za firme
  const [firms, setFirms] = useState([]);

  // Novo stanje za odabranu firmu
  const [selectedFirm, setSelectedFirm] = useState(''); // Početna vrijednost je '' što znači "Sve"

  const { currentUser } = useContext(AuthContext);

  const PLATFORM_GLOVO = 'glovo';
  const PLATFORM_WOLT = 'wolt';

  // Stanje za upravljanje vidljivošću FirmManager modala
  const [isFirmManagerOpen, setIsFirmManagerOpen] = useState(false);

  const [isArchivedPersonsModalOpen, setIsArchivedPersonsModalOpen] = useState(false);
  const [isUploadedWeeksModalOpen, setIsUploadedWeeksModalOpen] = useState(false);
  const [isPersonFormOpen, setIsPersonFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('obracun');
  const [dailyMetricsData, setDailyMetricsData] = useState([]);
  const [monthlyNightHoursMap, setMonthlyNightHoursMap] = useState({});
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [obracunRates, setObracunRates] = useState(DEFAULT_OBRACUN_RATES);
  const migrationRanRef = useRef(false);

  // Funkcija za dohvaćanje svih firmi
  const fetchFirms = async () => {
    try {
      const firmsCollection = collection(db, 'firms');
      const q = query(firmsCollection);
      const querySnapshot = await getDocs(q);
      const firmsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFirms(firmsData);
    } catch (error) {
      console.error('Greška pri dohvaćanju firmi:', error);
    }
  };

  // Funkcija za provjeru i kreiranje Default firme ako ne postoji
  const ensureDefaultFirm = async () => {
    try {
      const defaultFirmRef = doc(db, 'firms', 'default');
      const defaultFirmSnap = await getDocs(query(collection(db, 'firms'), where('__name__', '==', 'default')));
      if (defaultFirmSnap.empty) {
        await setDoc(defaultFirmRef, {
          name: 'Default',
          createdAt: new Date()
        });
        console.log('Zadana firma kreirana.');
        setFirms(prevFirms => [...prevFirms, { id: 'default', name: 'Default', createdAt: new Date() }]);
      } else {
        const defaultFirm = defaultFirmSnap.docs[0].data();
        setFirms(prevFirms => [...prevFirms, { id: 'default', ...defaultFirm }]);
        console.log('Zadana firma već postoji.');
      }
    } catch (error) {
      console.error('Greška pri provjeri/zadavanju firme:', error);
    }
  };

  const fetchUserPeriodData = async () => {
    try {
      const qUserPeriod = query(
        collection(db, 'userPeriodData'),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth)
      );
      const querySnapshot = await getDocs(qUserPeriod);
      setUserPeriodData(buildUserPeriodDataMap(querySnapshot.docs));
    } catch (e) {
      console.error('Greška pri dohvaćanju user period data:', e);
    }
  };

  // Funkcija za dohvaćanje učitanih tjedana iz "uploadedWeeks" kolekcije
  const fetchUploadedWeeks = async () => {
    try {
      let qUploadedWeeks = query(
        collection(db, 'uploadedWeeks'),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth)
      );

      // Ako je odabrana firma, dodajemo filter za firmId
      if (selectedFirm !== '') {
        qUploadedWeeks = query(qUploadedWeeks, where('firmId', '==', selectedFirm));
      }

      const querySnapshot = await getDocs(qUploadedWeeks);
      const weeks = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        week: doc.data().week,
        platform: doc.data().platform || PLATFORM_GLOVO,
        filename: doc.data().filename,
        year: doc.data().year,
        month: doc.data().month,
        firmId: doc.data().firmId // Dodajemo firmId
      }));
      setUploadedWeeks(weeks);
    } catch (e) {
      console.error('Greška pri dohvaćanju uploadedWeeks:', e);
    }
  };

  // Dohvaćanje podataka po periodu kad se promijene godina, mjesec, period ili firma
  useEffect(() => {
    fetchUserPeriodData();
    fetchUploadedWeeks();
  }, [selectedYear, selectedMonth, selectedFirm]);

  const fetchUsers = async () => {
    try {
      const qUsers = query(collection(db, 'users'));
      const querySnapshot = await getDocs(qUsers);
      const users = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const withAliases = addLegacyFinancialAliases(data);
  
        return {
          id: doc.id,
          name: data.name || '',
          email: data.email || 'nema',
          city: data.city || '',
          comment: data.comment || '',
          tekuciRacun: data.tekuciRacun || '',
          zasticeniRacun: data.zasticeniRacun || '',
          iban: data.iban || '',
          riderID: data.riderID || '',
          glovoId: data.glovoId || data.riderID || '',
          woltId: data.woltId || '',
          vozilo: data.vozilo || '',
          vlasnik: data.vlasnik || '',
          registracija: data.registracija || '',
          firmId: data.firmId || 'default',
          appKnjig: toNumber(withAliases.appKnjig),
          doprinosi: toNumber(withAliases.doprinosi),
          fee: toNumber(data.fee),
          renta: toNumber(withAliases.renta),
          rentSmjestaj: toNumber(data.rentSmjestaj),
          oprema: toNumber(data.oprema),
          akontacija: toNumber(withAliases.akontacija),
          gorivo: toNumber(withAliases.gorivo),
          ostalo: toNumber(data.ostalo),
          bonus: toNumber(data.bonus),
          tax: toNumber(withAliases.tax),
          rent: toNumber(withAliases.rent),
          petrol: toNumber(withAliases.petrol),
          acom: toNumber(withAliases.acom),
          utility: toNumber(withAliases.utility),
          sim: toNumber(withAliases.sim),
          advance: toNumber(withAliases.advance),
          // New fields for 'Stranac' (Foreigners)
          tarifa: toNumber(data.tarifa),
          depozit: toNumber(data.depozit),
          struja: toNumber(withAliases.struja),
          glovoTax: toNumber(withAliases.glovoTax),
          startMonth: data.startMonth || 9,
          startYear: data.startYear || 2024,
          archived: data.archived || false,
          archiveMonth: data.archiveMonth || null,
          archiveYear: data.archiveYear || null,
          imageUrl: data.imageUrl || null,
        };
      });
      setPeople(users);
      console.log('Dohvaćeni korisnici:', users);
    } catch (e) {
      console.error('Greška pri dohvaćanju korisnika:', e);
    }
  };
  

  // Funkcija za dohvaćanje zarada iz Firestorea
  const fetchEarnings = async () => {
    try {
      const qEarnings = query(
        collection(db, 'earnings'),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth)
      );

      const querySnapshot = await getDocs(qEarnings);
      const earnings = querySnapshot.docs.map((docSnap) => {
        const raw = docSnap.data();
        const normalized = normalizeWeekAndPlatform(raw.week, raw.platform);
        return {
          id: docSnap.id,
          ...raw,
          week: normalized?.week ?? raw.week,
          platform: normalized?.platform ?? (raw.platform || 'glovo'),
          amount: toNumber(raw.amount),
        };
      });
      setEarningsData(earnings);
    } catch (e) {
      console.error('Greška pri dohvaćanju zarada:', e);
    }
  };

  const fetchDailyMetrics = async () => {
    try {
      const qMetrics = query(collection(db, 'dailyMetrics'), where('year', '==', selectedYear));
      const snapshot = await getDocs(qMetrics);
      setDailyMetricsData(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Greška pri dohvaćanju dailyMetrics:', e);
    }
  };

  const fetchMonthlyNightHours = async () => {
    try {
      const qNight = query(collection(db, 'monthlyNightHours'), where('year', '==', selectedYear));
      const snapshot = await getDocs(qNight);
      const map = {};
      snapshot.docs.forEach((d) => {
        const data = d.data();
        if (data?.userId && data?.month) {
          map[`${data.userId}_${data.month}`] = toNumber(data.hours);
        }
      });
      setMonthlyNightHoursMap(map);
    } catch (e) {
      console.error('Greška pri dohvaćanju monthlyNightHours:', e);
    }
  };

  const fetchHolidays = async () => {
    try {
      const qHolidays = query(collection(db, 'holidays'), where('year', '==', selectedYear));
      const snapshot = await getDocs(qHolidays);
      const map = {};
      snapshot.docs.forEach((d) => {
        const data = d.data();
        const key =
          data.dateKey ||
          `${data.year}-${String(data.month).padStart(2, '0')}-${String(data.day).padStart(2, '0')}`;
        if (key) map[key] = data.name || 'Praznik';
      });
      setHolidaysByDate(map);
    } catch (e) {
      console.error('Greška pri dohvaćanju holidays:', e);
    }
  };

  const fetchObracunRates = async () => {
    try {
      const snap = await getDoc(doc(db, 'appSettings', 'satiObracunRates'));
      if (!snap.exists()) return;
      const data = snap.data() || {};
      const rates = {
        sunday: toNumber(data.sunday),
        holiday: toNumber(data.holiday),
        overtime: toNumber(data.overtime),
        night: toNumber(data.night),
      };
      setObracunRates(rates);
    } catch (e) {
      console.error('Greška pri dohvaćanju obračun rates:', e);
    }
  };

  const runDataMigrations = async () => {
    if (migrationRanRef.current) return;
    migrationRanRef.current = true;

    try {
      const [usersSnap, periodSnap, earningsSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'userPeriodData')),
        getDocs(collection(db, 'earnings')),
      ]);

      const migrationOps = [];

      usersSnap.forEach((docSnap) => {
        const patch = getFinancialMigrationPatch(docSnap.data());
        if (Object.keys(patch).length) migrationOps.push(updateDoc(docSnap.ref, patch));
      });

      periodSnap.forEach((docSnap) => {
        const patch = getFinancialMigrationPatch(docSnap.data());
        if (Object.keys(patch).length) migrationOps.push(updateDoc(docSnap.ref, patch));
      });

      earningsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const normalized = normalizeWeekAndPlatform(data.week, data.platform);
        if (!normalized) return;
        const patch = {};
        if (data.week !== normalized.week) patch.week = normalized.week;
        if (data.platform !== normalized.platform) patch.platform = normalized.platform;
        if (Object.keys(patch).length) migrationOps.push(updateDoc(docSnap.ref, patch));
      });

      if (migrationOps.length) {
        await Promise.all(migrationOps);
        console.log(`Migrirano zapisa: ${migrationOps.length}`);
      }
    } catch (e) {
      console.error('Greška pri migraciji podataka:', e);
    }
  };

  // Dohvaćanje firmi i osiguravanje da Default firma postoji kada je korisnik prijavljen
  useEffect(() => {
    if (currentUser) {
      ensureDefaultFirm();
      fetchFirms();
      runDataMigrations();
    }
  }, [currentUser]);

  // Dohvaćanje korisnika kada je korisnik prijavljen
  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser]);

  // Dohvaćanje zarada kada je korisnik prijavljen ili se promijene filteri
  useEffect(() => {
    if (!currentUser) return;
    Promise.all([
      fetchEarnings(),
      fetchUploadedWeeks(),
      fetchDailyMetrics(),
      fetchMonthlyNightHours(),
      fetchHolidays(),
      fetchObracunRates(),
    ]);
  }, [currentUser, selectedYear, selectedMonth, selectedFirm]);

  // Funkcija za dodavanje nove osobe
  const addPerson = async (person) => {
    // Provjera jedinstvenosti Rider ID-a
    const riderIDQuery = query(
      collection(db, 'users'),
      where('riderID', '==', person.riderID)
    );
    const riderIDSnapshot = await getDocs(riderIDQuery);
    if (!riderIDSnapshot.empty) {
      alert('Rider ID već postoji. Molimo unesite jedinstveni Rider ID.');
      return;
    }

    // Provjera da li je odabrana firma validna
    const selectedFirmObj = firms.find(firm => firm.id === person.firmId);
    if (!selectedFirmObj) {
      alert('Odabrana firma ne postoji.');
      return;
    }

    const personPayload = addLegacyFinancialAliases({
      ...person,
      iban: person.iban || '',
    });

    // Spremanje u Firestore
    try {
      const docRef = await addDoc(collection(db, 'users'), personPayload);
      console.log('Korisnik dodan s ID-om:', docRef.id);
      // Ažuriranje lokalnog stanja
      setPeople([...people, { id: docRef.id, ...personPayload }]);
      alert('Korisnik uspješno dodan!');
    } catch (e) {
      console.error('Greška pri dodavanju korisnika:', e);
      alert('Došlo je do greške prilikom dodavanja korisnika. Pokušajte ponovno.');
    }
  };

  const normalizeImportKey = (key) => String(key ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const normalizeImportRow = (row = {}) => {
    const out = {};
    Object.keys(row).forEach((key) => {
      out[normalizeImportKey(key)] = row[key];
    });
    return out;
  };

  const pickFirstValue = (obj, keys = []) => {
    for (const key of keys) {
      const normalizedKey = normalizeImportKey(key);
      if (obj[normalizedKey] !== undefined && obj[normalizedKey] !== null && obj[normalizedKey] !== '') {
        return obj[normalizedKey];
      }
    }
    return undefined;
  };

  const normalizeId = (raw) => {
    if (typeof raw === 'string') return raw.trim().toLowerCase();
    if (raw == null) return '';
    return String(raw).trim().toLowerCase();
  };

  // Funkcija za ažuriranje zarada
  const updateEarnings = async (week, data, filename, platform = PLATFORM_GLOVO) => {
    const year = selectedYear;
    const month = selectedMonth;
    const firmId = selectedFirm;
    const uniqueWeekId = `${year}-${month}-${week}-${firmId}`;

    if (firmId === '') {
      alert('Molimo odaberite firmu prije upload-a.');
      return;
    }

    const existingWeek = uploadedWeeks.find(
      (w) => w.week === week && w.year === year && w.month === month && w.firmId === firmId
    );
    if (existingWeek) {
      alert(`Tjedan ${week} za ${month}/${year} i firmu ${getFirmName(firmId)} je već uvezen.`);
      return;
    }

    const cfg = platform === PLATFORM_WOLT ? EARNINGS_IMPORT_CONFIG.wolt : EARNINGS_IMPORT_CONFIG.glovo;
    const updatedUserIds = new Set();

    for (const item of data) {
      const normalizedItem = normalizeImportRow(item);
      const externalId = normalizeId(pickFirstValue(normalizedItem, cfg.idColumnKeys));
      const amountRaw = pickFirstValue(normalizedItem, cfg.amountColumnKeys);
      const amount = toNumber(amountRaw);
      if (!externalId) continue;

      const person = people.find((p) => {
        if (platform === PLATFORM_WOLT) {
          return normalizeId(p.woltId) === externalId;
        }
        return normalizeId(p.glovoId || p.riderID) === externalId;
      });
      if (!person || amount === 0) continue;

      try {
        const validation = validateEarningRecord({
          userId: person.id,
          year,
          month,
          week,
          amount,
          firmId,
          platform,
        });
        if (!validation.valid) continue;
        await addDoc(collection(db, 'earnings'), validation.payload);
        updatedUserIds.add(person.id);
      } catch (e) {
        console.error('Greška pri spremanju zarade:', e);
      }
    }
  
    // Nakon što su svi podaci procesirani, dodajemo tjedan u 'uploadedWeeks'
    try {
      await setDoc(doc(db, 'uploadedWeeks', uniqueWeekId), {
        week,
        platform,
        year,
        month,
        filename,
        firmId, // Dodajemo firmId
      });
      setUploadedWeeks([...uploadedWeeks, { id: uniqueWeekId, week, platform, filename, year, month, firmId }]);
      alert(`Tjedan ${week} za ${month}/${year} i firmu ${getFirmName(firmId)} je uspješno uvezen.`);
    } catch (e) {
      console.error('Greška pri dodavanju u uploadedWeeks:', e);
      alert('Došlo je do greške prilikom označavanja tjedna kao učitanog.');
    }
  
    // **Nova logika za kreiranje userPeriodData s defaultnim vrijednostima**
    try {
      for (const userId of updatedUserIds) {
        const person = people.find((p) => p.id === userId);
        if (!person) continue;
  
        const userPeriodQuery = query(
          collection(db, 'userPeriodData'),
          where('userId', '==', person.id),
          where('year', '==', year),
          where('month', '==', month)
        );
        const userPeriodSnapshot = await getDocs(userPeriodQuery);
  
        if (userPeriodSnapshot.empty) {
          const newUserPeriodData = {
            userId: person.id,
            year,
            month,
            fee: person.fee || 0,
            appKnjig: person.appKnjig || person.acom || 0,
            doprinosi: person.doprinosi || person.sim || 0,
            renta: person.renta || person.rent || 0,
            rentSmjestaj: person.rentSmjestaj || 0,
            oprema: person.oprema || 0,
            akontacija: person.akontacija || person.advance || 0,
            gorivo: person.gorivo || person.petrol || 0,
            ostalo: person.ostalo || 0,
            bonus: person.bonus || 0,
            napomena: '',
            zaradaTotal: 0,
            feeAmount: 0,
            total1: 0,
            total2: 0,
            tax: person.tax || person.glovoTax || 0,
            rent: person.rent || person.renta || 0,
            petrol: person.petrol || person.gorivo || 0,
            acom: person.acom || person.appKnjig || 0,
            utility: person.utility || person.struja || 0,
            sim: person.sim || person.doprinosi || 0,
            advance: person.advance || person.akontacija || 0,
          };
        
          newUserPeriodData.tarifa = person.tarifa || 0;
          newUserPeriodData.depozit = person.depozit || 0;
          newUserPeriodData.struja = person.struja || 0;
          newUserPeriodData.glovoTax = person.glovoTax || 0;
        
          await addDoc(collection(db, 'userPeriodData'), newUserPeriodData);
          console.log(`Kreiran userPeriodData za korisnika ${person.name} za ${month}/${year}`);
        }
      }
    } catch (e) {
      console.error('Greška pri kreiranju userPeriodData:', e);
    }
  
    // Nakon spremanja, osvježavamo podatke
    refreshData();
  };

  // Funkcija za dohvaćanje imena firme na temelju firmId
  const getFirmName = (firmId) => {
    const firm = firms.find(firm => firm.id === firmId);
    return firm ? firm.name : 'Default';
  };

  // Funkcija za brisanje zarada za određeni tjedan i firmu
  const deleteEarningsForWeek = async (week, firmId) => { // Izmijenjeno da prima firmId
    if (!window.confirm(`Jeste li sigurni da želite obrisati sve zarade za tjedan ${week} i firmu ${getFirmName(firmId)}?`)) {
      return;
    }

    try {
      // Dohvaćanje učitanih tjedana koji odgovaraju odabranom tjednu, godini, mjesecu i firmi
      const qWeeks = query(
        collection(db, 'uploadedWeeks'),
        where('week', '==', week),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth),
        where('firmId', '==', firmId) // Dodajemo filter za firmId
      );

      const weeksSnapshot = await getDocs(qWeeks);

      if (weeksSnapshot.empty) {
        alert('Nema učitanih tjedana za ovaj kriterij.');
        return;
      }

      // Brisanje zarada i uploadedWeeks za svaki pronađeni tjedan
      for (const weekDoc of weeksSnapshot.docs) {
        const weekData = weekDoc.data();
        const firmId = weekData.firmId;

        // Brisanje zarada iz "earnings" kolekcije za taj tjedan i firmu
        const qEarnings = query(
          collection(db, 'earnings'),
          where('year', '==', selectedYear),
          where('month', '==', selectedMonth),
          where('week', '==', week),
          where('firmId', '==', firmId)
        );
        const earningsSnapshot = await getDocs(qEarnings);

        const deletePromises = earningsSnapshot.docs.map((docSnap) =>
          deleteDoc(doc(db, 'earnings', docSnap.id))
        );

        await Promise.all(deletePromises);
        console.log(`Sve zarade za tjedan ${week} i firmu ${getFirmName(firmId)} su izbrisane.`);

        // Brisanje iz "uploadedWeeks" kolekcije
        await deleteDoc(doc(db, 'uploadedWeeks', weekDoc.id));
        console.log(`Tjedan ${week} za firmu ${getFirmName(firmId)} je izbrisan iz uploadedWeeks.`);

        // Ažuriranje stanja uploadedWeeks
        setUploadedWeeks(uploadedWeeks.filter((w) => w.id !== weekDoc.id));
      }

      // Ažuriramo zarade nakon brisanja
      refreshData();
    } catch (e) {
      console.error(`Greška pri brisanju zarada za tjedan ${week} i firmu ${firmId}:`, e);
    }
  };

  // Funkcija za ažuriranje osobe u lokalnom stanju
  const handleUpdatePerson = (updatedPerson) => {
    setPeople((prevPeople) =>
      prevPeople.map((person) =>
        person.id === updatedPerson.id ? updatedPerson : person
      )
    );
  };

  // Funkcija za osvježavanje podataka
  const refreshData = () => {
    fetchEarnings();
    fetchUserPeriodData();
    fetchUploadedWeeks();
    fetchFirms(); // Osvježava i firme, u slučaju promjena
    fetchUsers(); // Osvježava i korisnike
  };

  // Funkcija za prebacivanje korisnika u Default firmu prilikom brisanja firme
  const moveUsersToDefaultFirm = async (firmIdToDelete) => {
    try {
      // Dohvaćanje svih korisnika koji pripadaju obrisanoj firmi
      const qUsers = query(
        collection(db, 'users'),
        where('firmId', '==', firmIdToDelete)
      );
      const usersSnapshot = await getDocs(qUsers);

      const updatePromises = usersSnapshot.docs.map((docSnap) =>
        updateDoc(doc(db, 'users', docSnap.id), { firmId: 'default' })
      );

      await Promise.all(updatePromises);
      console.log(`Sve osobe iz firme ${firmIdToDelete} prebačene u Default firmu.`);
      // Osvježavanje podataka nakon prebacivanja
      refreshData();
    } catch (error) {
      console.error('Greška pri prebacivanju korisnika u Default firmu:', error);
      alert('Došlo je do greške prilikom prebacivanja korisnika u Default firmu.');
    }
  };

  const unarchivePerson = async (personId) => {
    try {
      await updateDoc(doc(db, 'users', personId), {
        archived: false,
        archiveMonth: null,
        archiveYear: null,
      });
      setPeople((prevPeople) =>
        prevPeople.map((person) =>
          person.id === personId ? { ...person, archived: false, archiveMonth: null, archiveYear: null } : person
        )
      );
      alert('Osoba uspješno odarhivirana!');
    } catch (e) {
      console.error('Greška pri odarhiviranju korisnika:', e);
      alert('Došlo je do greške prilikom odarhiviranja korisnika. Pokušajte ponovno.');
    }
  };

  const handleExportPersonTable = async () => {
    const numOfWeeks = 5;
    const startWeek = 1;

    const filteredPeople = people
      .filter((person) => {
        const firmMatch = selectedFirm === '' || person.firmId === selectedFirm;
        const dateMatch =
          (person.startYear < selectedYear ||
            (person.startYear === selectedYear && person.startMonth <= selectedMonth)) &&
          (!person.archived ||
            person.archiveYear > selectedYear ||
            (person.archiveYear === selectedYear && person.archiveMonth > selectedMonth));
        const notArchived = !person.archived;
        return firmMatch && dateMatch && notArchived;
      })
      .sort((a, b) => {
        if (selectedFirm === '') {
          // Sortiranje po imenu firme ako firma nije odabrana
          const firmA = firms.find((firm) => firm.id === a.firmId)?.name || '';
          const firmB = firms.find((firm) => firm.id === b.firmId)?.name || '';
          if (firmA === firmB) {
            return a.id.localeCompare(b.id);
          }
          return firmA.localeCompare(firmB);
        } else {
          // Ako je firma odabrana, sortiranje po ID-u osobe
          return a.id.localeCompare(b.id);
        }
      });
  
    // Priprema podataka za izvoz
    const exportData = [];
  
    // Inicijalizacija suma
    const sums = {
      // Sume po tjednima
      ...Array.from({ length: numOfWeeks }, (_, i) => [`tjedan${startWeek + i}`, 0]).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {}
      ),
      // Wolt zarade
      w1: 0,
      w2: 0,
      // Ostale sume
      zaradaTotal: 0,
      feeIznos: 0,
      total1: 0,
      tax: 0,
      rent: 0,
      petrol: 0,
      acom: 0,
      utility: 0,
      sim: 0,
      advance: 0,
      nedelja: 0,
      praznici: 0,
      prekovremeno: 0,
      nocni: 0,
      ostalo: 0,
      bonus: 0,
      total2: 0,
    };
  
    // Iterating through filtered people
    for (const person of filteredPeople) {
      const monthlyDoc = userPeriodData[person.id];
      const personEarnings = getPersonEarningsForMonth(
        earningsData,
        person.id,
        selectedYear,
        selectedMonth
      );
      const { weekTotals, zaradaTotal } = sumEarningsByWeek(personEarnings);

      const fee = getMonthlyScalar(person, monthlyDoc, 'fee');
      const { feeIznos, total1 } = computeFeeTotals(zaradaTotal, fee);
      const tax = getMonthlyFinancial(person, monthlyDoc, 'tax');
      const rent = getMonthlyFinancial(person, monthlyDoc, 'rent');
      const petrol = getMonthlyFinancial(person, monthlyDoc, 'petrol');
      const acom = getMonthlyFinancial(person, monthlyDoc, 'acom');
      const utility = getMonthlyFinancial(person, monthlyDoc, 'utility');
      const sim = getMonthlyFinancial(person, monthlyDoc, 'sim');
      const advance = getMonthlyFinancial(person, monthlyDoc, 'advance');
      const bonus = getMonthlyScalar(person, monthlyDoc, 'bonus');
      const ostalo = getMonthlyScalar(person, monthlyDoc, 'ostalo');

      const hoursSummary = computeMonthlyHourSummary(
        person.id,
        selectedYear,
        selectedMonth,
        dailyMetricsData,
        holidaysByDate,
        monthlyNightHoursMap
      );
      const autoAmounts = computeAutoAdditionsFromHours(hoursSummary, obracunRates);
      const total2 = computeTotal2({
        total1,
        tax,
        rent,
        petrol,
        acom,
        utility,
        sim,
        advance,
        sunday: autoAmounts.sunday,
        holiday: autoAmounts.holiday,
        overtime: autoAmounts.overtime,
        night: autoAmounts.night,
        bonus,
        ostalo,
      });

      const rowData = {
        name: person.name,
        riderID: person.riderID || person.glovoId,
        firm: getFirmName(person.firmId),
        tjedan1: weekTotals.tjedan1.toFixed(2),
        tjedan2: weekTotals.tjedan2.toFixed(2),
        tjedan3: weekTotals.tjedan3.toFixed(2),
        tjedan4: weekTotals.tjedan4.toFixed(2),
        tjedan5: weekTotals.tjedan5.toFixed(2),
        w1: weekTotals.w1.toFixed(2),
        w2: weekTotals.w2.toFixed(2),
        zaradaTotal: zaradaTotal.toFixed(2),
        fee,
        feeIznos: feeIznos.toFixed(2),
        total1: total1.toFixed(2),
        tax: tax.toFixed(2),
        rent: rent.toFixed(2),
        petrol: petrol.toFixed(2),
        acom: acom.toFixed(2),
        utility: utility.toFixed(2),
        sim: sim.toFixed(2),
        advance: advance.toFixed(2),
        nedelja: autoAmounts.sunday.toFixed(2),
        praznici: autoAmounts.holiday.toFixed(2),
        prekovremeno: autoAmounts.overtime.toFixed(2),
        nocni: autoAmounts.night.toFixed(2),
        bonus: bonus.toFixed(2),
        ostalo: ostalo.toFixed(2),
        total2: total2.toFixed(2),
      };

      // Adding row to exportData
      exportData.push(rowData);

      for (let i = 0; i < numOfWeeks; i++) {
        const weekKey = `tjedan${startWeek + i}`;
        sums[weekKey] += parseFloat(rowData[weekKey]);
      }
      sums.w1 += weekTotals.w1;
      sums.w2 += weekTotals.w2;
      sums.zaradaTotal += zaradaTotal;
      sums.feeIznos += feeIznos;
      sums.total1 += total1;
      sums.tax += tax;
      sums.rent += rent;
      sums.petrol += petrol;
      sums.acom += acom;
      sums.utility += utility;
      sums.sim += sim;
      sums.advance += advance;
      sums.nedelja += autoAmounts.sunday;
      sums.praznici += autoAmounts.holiday;
      sums.prekovremeno += autoAmounts.overtime;
      sums.nocni += autoAmounts.night;
      sums.ostalo += ostalo;
      sums.bonus += bonus;
      sums.total2 += total2;
    }
  
    // Generiranje Excel datoteke
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Obračun');
  
    // Definicija stupaca
    const columns = [
      { header: 'Ime', key: 'name', width: 20 },
      { header: 'Rider ID', key: 'riderID', width: 15 },
      { header: 'Firma', key: 'firm', width: 20 },
      ...Array.from({ length: numOfWeeks }, (_, i) => ({
        header: `Tjedan ${startWeek + i}`,
        key: `tjedan${startWeek + i}`,
        width: 10,
      })),
      { header: 'W1', key: 'w1', width: 10 },
      { header: 'W2', key: 'w2', width: 10 },
      { header: 'Zarada Total', key: 'zaradaTotal', width: 15 },
      { header: 'Fee %', key: 'fee', width: 10 },
      { header: 'FEE Iznos', key: 'feeIznos', width: 15 },
      { header: 'Total 1', key: 'total1', width: 15 },
      { header: 'Tax', key: 'tax', width: 12 },
      { header: 'Rent', key: 'rent', width: 12 },
      { header: 'Petrol', key: 'petrol', width: 12 },
      { header: 'Acom', key: 'acom', width: 12 },
      { header: 'Utility', key: 'utility', width: 12 },
      { header: 'Sim', key: 'sim', width: 12 },
      { header: 'Advance', key: 'advance', width: 12 },
      { header: 'Nedelja', key: 'nedelja', width: 12 },
      { header: 'Praznici', key: 'praznici', width: 12 },
      { header: 'Prekovremeno', key: 'prekovremeno', width: 12 },
      { header: 'Noćni', key: 'nocni', width: 12 },
      { header: 'Bonus', key: 'bonus', width: 12 },
      { header: 'Ostalo', key: 'ostalo', width: 12 },
      { header: 'Total 2', key: 'total2', width: 15 },
    ];
  
    worksheet.columns = columns;
  
    // Dodavanje podataka
    exportData.forEach((row) => {
      worksheet.addRow(row);
    });
  
    // Dodavanje suma
    const sumsRowData = {
      name: 'Suma',
      ...Array.from({ length: numOfWeeks }, (_, i) => {
        const weekKey = `tjedan${startWeek + i}`;
        return { [weekKey]: sums[weekKey].toFixed(2) };
      }).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      w1: sums.w1.toFixed(2),
      w2: sums.w2.toFixed(2),
      zaradaTotal: sums.zaradaTotal.toFixed(2),
      feeIznos: sums.feeIznos.toFixed(2),
      total1: sums.total1.toFixed(2),
      tax: sums.tax.toFixed(2),
      rent: sums.rent.toFixed(2),
      petrol: sums.petrol.toFixed(2),
      acom: sums.acom.toFixed(2),
      utility: sums.utility.toFixed(2),
      sim: sums.sim.toFixed(2),
      advance: sums.advance.toFixed(2),
      nedelja: sums.nedelja.toFixed(2),
      praznici: sums.praznici.toFixed(2),
      prekovremeno: sums.prekovremeno.toFixed(2),
      nocni: sums.nocni.toFixed(2),
      ostalo: sums.ostalo.toFixed(2),
      bonus: sums.bonus.toFixed(2),
      total2: sums.total2.toFixed(2),
    };
  
    // Dodavanje reda sa sumama
    worksheet.addRow(sumsRowData);
  
    // Stiliziranje zaglavlja i suma
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(worksheet.lastRow.number).font = { bold: true };
  
    // Određivanje imena firme za naziv datoteke
    const firmNameForFile = selectedFirm
      ? getFirmName(selectedFirm).replace(/[^a-z0-9]/gi, '_')
      : 'sve';
  
    // Preuzimanje datoteke s imenom koje uključuje ime firme
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `Obračun_${firmNameForFile}_${selectedMonth}_${selectedYear}.xlsx`
    );
  };
  
  return (
    <div>
      {currentUser ? (
        <div>
          <div className="filter-form">
            <div className="filter-controls">
              <label>Firma:</label>
              <select
                value={selectedFirm}
                onChange={(e) => setSelectedFirm(e.target.value)}
              >
                <option value="">Sve</option>
                {firms.filter(firm => firm.id !== 'default').map((firm) => (
                  <option key={firm.id} value={firm.id}>
                    {firm.name}
                  </option>
                ))}
              </select>

              <label>Godina:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {Array.from({ length: new Date().getFullYear() + 1 - 2022 + 1 }, (_, i) => 2022 + i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <label>Mjesec:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            <div className="toolbar-buttons">
              <button onClick={handleExportPersonTable} className="export-button">
                Izvezi obračun tablicu
              </button>

              <button onClick={() => setIsFirmManagerOpen(true)} className="manage-firms-button">
                Upravljaj firmama
              </button>

              <button onClick={() => setIsArchivedPersonsModalOpen(true)} className="manage-firms-button">
                Arhivirane osobe
              </button>

              <button onClick={() => setIsUploadedWeeksModalOpen(true)} className="manage-firms-button">
                Učitani tjedni
              </button>

              <button onClick={() => setIsPersonFormOpen(true)} className="add-person-button">
                Dodaj osobu
              </button>
            </div>
          </div>

          {/* Prosljeđujemo listu firmi PersonForm */}
          <Modal
            isOpen={isPersonFormOpen}
            onRequestClose={() => setIsPersonFormOpen(false)}
            contentLabel="Dodaj osobu"
            className="modal"
            overlayClassName="overlay"
          >
            <PersonForm onAddPerson={addPerson} firms={firms} />
            <button onClick={() => setIsPersonFormOpen(false)} className="close-modal-button">
              Zatvori
            </button>
          </Modal>

          {/* Modal za upravljanje firmama */}
          <FirmManager 
            isOpen={isFirmManagerOpen}
            onClose={() => setIsFirmManagerOpen(false)}
            firms={firms} 
            setFirms={setFirms} 
            moveUsersToDefaultFirm={moveUsersToDefaultFirm}
          />

          <Modal
              isOpen={isArchivedPersonsModalOpen}
              onRequestClose={() => setIsArchivedPersonsModalOpen(false)}
              contentLabel="Arhivirane osobe"
              className="modal archived-persons-modal"
              overlayClassName="overlay"
            >
              <div className="modal-header">
                <h2>Arhivirane osobe</h2>
                <button onClick={() => setIsArchivedPersonsModalOpen(false)} className="close-modal-button">&times;</button>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Ime</th>
                    <th>Firma</th>
                    <th>Arhivirano</th>
                    <th>Akcija</th>
                  </tr>
                </thead>
                <tbody>
                  {people
                    .filter((person) => person.archived)
                    .sort((a, b) => {
                      // Prvo pretvaramo mjesec/godinu u datum za usporedbu
                      const dateA = new Date(a.archiveYear, a.archiveMonth - 1);
                      const dateB = new Date(b.archiveYear, b.archiveMonth - 1);
                      // Sortiramo silazno (od najnovijeg prema najstarijem)
                      return dateB - dateA;
                    })
                    .map((person) => (
                      <tr key={person.id}>
                        <td>{person.name}</td>
                        <td>{getFirmName(person.firmId)}</td>
                        <td>
                          {person.archiveMonth}/{person.archiveYear}
                        </td>
                        <td>
                          <button className='manage-firms-button' onClick={() => unarchivePerson(person.id)}>Odarhiviraj</button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Modal>

          {/* Modal za prikaz učitanih tjedana */}
          <Modal
            isOpen={isUploadedWeeksModalOpen}
            onRequestClose={() => setIsUploadedWeeksModalOpen(false)}
            contentLabel="Učitani tjedni"
            className="modal uploaded-weeks-modal"
            overlayClassName="overlay"
          >
            <button
              className="close-modal-button"
              onClick={() => setIsUploadedWeeksModalOpen(false)}
            >
              ×
            </button>
            <h2>Učitani tjedni</h2>
            {uploadedWeeks.length > 0 ? (
              <ul>
                {uploadedWeeks
                  .sort((a, b) => {
                    // Prvo sortiraj po firmi
                    const firmAName = getFirmName(a.firmId);
                    const firmBName = getFirmName(b.firmId);
                    const firmCompare = firmAName.localeCompare(firmBName);
                    if (firmCompare !== 0) return firmCompare;

                    // Ako su firme iste, sortiraj po platformi pa po tjednu
                    const platformA = a.platform || PLATFORM_GLOVO;
                    const platformB = b.platform || PLATFORM_GLOVO;
                    if (platformA !== platformB) return platformA.localeCompare(platformB);

                    const weekOrder = (w) => {
                      if (w === 'w1') return 101;
                      if (w === 'w2') return 102;
                      return parseInt(w, 10) || 0;
                    };
                    return weekOrder(a.week) - weekOrder(b.week);
                  })
                  .map((week) => (
                    <li key={week.id}>
                      <div className="week-info">
                        <span className="firm-name">{getFirmName(week.firmId)}</span>
                        <span className="week-number">
                          {week.platform === PLATFORM_WOLT ? `Wolt ${String(week.week).toUpperCase()}` : `Glovo tjedan ${week.week}`}
                        </span>
                        <span className="filename">{week.filename}</span>
                      </div>
                      <button
                        className="delete-week-button"
                        onClick={() => deleteEarningsForWeek(week.week, week.firmId)}
                      >
                        IZBRIŠI
                      </button>
                    </li>
                  ))}
              </ul>
            ) : (
              <div className="no-weeks">Nema učitanih tjedana</div>
            )}
          </Modal>

          {/* Omotavanje tablice u scrollable div */}
          <div className="obracun-tabs">
            <button
              className={`obracun-tab-button ${activeTab === 'obracun' ? 'active' : ''}`}
              onClick={() => setActiveTab('obracun')}
            >
              Obračun tablica
            </button>
            <button
              className={`obracun-tab-button ${activeTab === 'isplata' ? 'active' : ''}`}
              onClick={() => setActiveTab('isplata')}
            >
              Isplata tablica
            </button>
          </div>

          {activeTab === 'obracun' && (
            <div className="table-container">
              <PersonTable
                people={people}
                earningsData={earningsData}
                userPeriodData={userPeriodData}
                updateEarnings={updateEarnings}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                onUpdatePerson={handleUpdatePerson}
                refreshData={refreshData}
                firms={firms}
                selectedFirm={selectedFirm}
                dailyMetricsData={dailyMetricsData}
                monthlyNightHoursMap={monthlyNightHoursMap}
                holidaysByDate={holidaysByDate}
                obracunRates={obracunRates}
              />
            </div>
          )}

          {activeTab === 'isplata' && (
            <div className="table-container">
              <PaymentTable
                people={people}
                earningsData={earningsData}
                userPeriodData={userPeriodData}
                selectedYear={selectedYear}
                selectedMonth={selectedMonth}
                selectedFirm={selectedFirm}
                firms={firms}
                dailyMetricsData={dailyMetricsData}
                monthlyNightHoursMap={monthlyNightHoursMap}
                holidaysByDate={holidaysByDate}
                obracunRates={obracunRates}
              />
            </div>
          )}
        </div>
      ) : (
        <Login />
      )}
    </div>
  );
}

export default App;
